import React from 'react';
import { AppBar, IconButton, Tooltip, Menu, MenuItem, Typography, Box, Divider, ListItemIcon, ListItemText, FormControl, Select, CircularProgress } from '@mui/material';

// Utility function to get JWT from cookie
const getJwtFromCookie = () => {
  const match = document.cookie.match(/(?:^|; )jwt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};
import MicIcon from '@mui/icons-material/Mic';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ENDPOINTS } from '../config/api';

type ToolbarOrientation = 'horizontal' | 'vertical' | 'bottom';
interface ToolbarProps {
  onThemeToggle: () => void;
  darkMode: boolean;
  onOpenSettings: () => void;
  onToggleAIResponse: () => void;
  showAIResponse: boolean;
  onScreenCapture: () => void;
  onLiveStream: () => void;
  isStreaming: boolean;
  isSourceSwitching?: boolean;
  onMicStream: () => void;
  isMicStreaming: boolean;
  micVolume?: number;
  audioSource: 'mic' | 'system' | 'both';
  onAudioSourceChange: (source: 'mic' | 'system' | 'both') => void;
  onToggleTranscription: () => void;
  showTranscription: boolean;
  videoSource: 'screen' | 'webcam';
  onVideoSourceChange: (source: 'screen' | 'webcam') => void;
  onWebcamStream: () => void;
  isWebcamStreaming: boolean;
  isWebcamDeviceSwitching?: boolean;
  availableWebcams: MediaDeviceInfo[];
  selectedWebcamId: string;
  onWebcamDeviceChange: (deviceId: string) => void;
  onGlobalVoiceCommand?: () => void;
  toolbarOrientation: ToolbarOrientation;
  toolbarSide: 'left' | 'right';
  onToggleToolbarOrientation: () => void;
  onToggleToolbarSide: () => void;
}

// Utility to fetch user info from backend
async function fetchUserData() {
  const jwt = getJwtFromCookie();
  if (!jwt) return null;
  
  try {
    const response = await fetch(ENDPOINTS.USERS_ME, {
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.data || data;
    }
  } catch (error) {
    console.error('Failed to fetch user data:', error);
  }
  
  return null;
}

const Toolbar: React.FC<ToolbarProps> = ({ 
  onThemeToggle, 
  darkMode, 
  onOpenSettings, 
  onToggleAIResponse, 
  showAIResponse, 
  onScreenCapture, 
  onLiveStream, 
  isStreaming, 
  isSourceSwitching,
  onMicStream, 
  isMicStreaming, 
  micVolume, 
  audioSource, 
  onAudioSourceChange, 
  onToggleTranscription, 
  showTranscription,
  videoSource,
  onVideoSourceChange,
  onWebcamStream,
  isWebcamStreaming,
  isWebcamDeviceSwitching,
  availableWebcams,
  selectedWebcamId,
  onWebcamDeviceChange,
  onGlobalVoiceCommand,
  toolbarOrientation,
  toolbarSide,
  onToggleToolbarOrientation,
  onToggleToolbarSide
}) => {
  // State to store user data
  const [user, setUser] = React.useState<any>(null);

  // Fetch user data on component mount
  React.useEffect(() => {
    const loadUserData = async () => {
      const userData = await fetchUserData();
      setUser(userData);
    };
    
    loadUserData();
  }, []);

  // For demonstration, the toolbar is a single draggable row at the top
  // (In a real app, you might want to allow reordering buttons, etc.)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [toolbarPosition, setToolbarPosition] = React.useState(0); // px from left

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSettings = () => {
    handleClose();
    onOpenSettings();
  };

  const handleLogout = async () => {
    // Remove JWT from localStorage and cookie
    localStorage.removeItem('jwt');
    
    // Clear JWT cookie by setting it to expire in the past
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const sameSite = isLocal ? 'SameSite=Lax' : 'SameSite=None';
    document.cookie = `jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.streamio.ai; ${sameSite}`;
    document.cookie = `jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${sameSite}`;
    
    // Call backend logout endpoint if available
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      // Ignore errors
    }
    // Redirect to landing page
    window.location.href = isLocal ? 'http://localhost:3000' : 'https://www.streamio.ai';
  };

  // Drag logic: just update left offset
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    // For now, just allow horizontal movement
    const offset = result.destination.index * 40; // 40px per step
    setToolbarPosition(offset);
  };

  // Compute toolbar position and layout
  const isVertical = toolbarOrientation === 'vertical';
  const isBottom = toolbarOrientation === 'bottom';
  const sidePosition = isVertical && toolbarSide === 'right' ? { right: 12, left: 'auto' } : { left: 12, right: 'auto' };
  const dragAxis = isVertical ? 'y' : 'x';

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="toolbar-droppable" direction={isVertical ? 'vertical' : 'horizontal'}>
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              position: 'fixed',
              ...(isVertical
                ? {
                    top: '50%',
                    ...sidePosition,
                    transform: 'translateY(-50%)',
                    flexDirection: 'column',
                    minWidth: 64,
                    width: 'auto',
                    minHeight: 340,
                    maxHeight: '90vh',
                    height: 'auto',
                  }
                : isBottom
                  ? {
                      bottom: 12,
                      left: `calc(50% + ${toolbarPosition}px)`,
                      transform: 'translateX(-50%)',
                      flexDirection: 'row',
                      minWidth: 340,
                      maxWidth: '100vw',
                      width: 'auto',
                      height: 64,
                    }
                  : {
                      top: 12,
                      left: `calc(50% + ${toolbarPosition}px)`,
                      transform: 'translateX(-50%)',
                      flexDirection: 'row',
                      minWidth: 340,
                      maxWidth: '100vw',
                      width: 'auto',
                      height: 64,
                    }),
              zIndex: 1301,
              borderRadius: 3,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              background: darkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              gap: isVertical ? 0.5 : 1,
              overflowX: isVertical ? 'hidden' : 'auto',
              overflowY: isVertical ? 'auto' : 'hidden',
              whiteSpace: isVertical ? 'normal' : 'nowrap',
              flexWrap: 'nowrap',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              transition: 'all 0.3s ease',
              '&:hover': {
                background: darkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.3)',
                boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.45)',
              },
            }}
            aria-label="Floating toolbar"
          >
            {/* Orientation Toggle Button */}
            <Tooltip title={
              isVertical
                ? 'Switch to bottom toolbar'
                : isBottom
                  ? 'Switch to horizontal toolbar'
                  : 'Switch to vertical toolbar'
            }>
              <span>
                <IconButton color="primary" size="large" onClick={onToggleToolbarOrientation}>
                  {isVertical ? <ArrowDownwardIcon /> : isBottom ? <ViewColumnIcon /> : <SwapVertIcon />}
                </IconButton>
              </span>
            </Tooltip>
            {/* Side Toggle Button (only in vertical mode) */}
            {isVertical && (
              <Tooltip title={toolbarSide === 'left' ? 'Move toolbar to right' : 'Move toolbar to left'}>
                <span>
                  <IconButton color="primary" size="large" onClick={onToggleToolbarSide}>
                    {toolbarSide === 'left' ? <ArrowForwardIosIcon /> : <ArrowBackIosNewIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Draggable draggableId="toolbar" index={0}>
              {(providedDraggable) => (
                <Box
                  ref={providedDraggable.innerRef}
                  {...providedDraggable.draggableProps}
                  {...providedDraggable.dragHandleProps}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isVertical ? 0.5 : 1,
                    flexDirection: isVertical ? 'column' : 'row',
                    width: '100%',
                  }}
                >
                  {/* Main controls */}
                  <Tooltip title="Voice Input (Global Command) (⌘⇧L)"><span><IconButton color="primary" size="large" onClick={onGlobalVoiceCommand}><HeadsetMicIcon /></IconButton></span></Tooltip>
                  <Tooltip title="Ask (⌘↩)"><span><IconButton color={showAIResponse ? 'primary' : 'default'} size="large" onClick={onToggleAIResponse}><QuestionAnswerIcon /></IconButton></span></Tooltip>
                  <Tooltip title="Capture screen"><span><IconButton color="primary" size="large" onClick={onScreenCapture}><VisibilityIcon /></IconButton></span></Tooltip>
                  
                  {/* Video Source Selector */}
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={videoSource}
                      onChange={e => onVideoSourceChange(e.target.value as 'screen' | 'webcam')}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Video Source' }}
                      sx={{ 
                        fontSize: 13,
                        background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 1,
                        '& .MuiSelect-select': {
                          color: darkMode ? 'white' : 'inherit',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                      MenuProps={isVertical ? {
                        anchorOrigin: { horizontal: toolbarSide === 'left' ? 'right' : 'left', vertical: 'top' },
                        transformOrigin: { horizontal: toolbarSide === 'left' ? 'left' : 'right', vertical: 'top' },
                        PaperProps: {
                          sx: {
                            marginLeft: toolbarSide === 'left' ? 2 : 0,
                            marginRight: toolbarSide === 'right' ? 2 : 0,
                            background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                          }
                        }
                      } : {
                        PaperProps: {
                          sx: {
                            background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                          }
                        }
                      }}
                    >
                      <MenuItem value="screen">Screen</MenuItem>
                      <MenuItem value="webcam">Webcam</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {/* Webcam Device Selector - only show when webcam is selected */}
                  {videoSource === 'webcam' && availableWebcams.length > 1 && (
                    <Tooltip title="Select webcam device">
                      <span>
                        <FormControl size="small" sx={{ minWidth: 120, ml: 1 }}>
                          <Select
                            value={selectedWebcamId || ''}
                            onChange={e => onWebcamDeviceChange(e.target.value)}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Webcam Device' }}
                        sx={{ 
                          fontSize: 13,
                          background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(20px)',
                          WebkitBackdropFilter: 'blur(20px)',
                          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          '& .MuiSelect-select': {
                            color: darkMode ? 'white' : 'inherit',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                        MenuProps={isVertical ? {
                          anchorOrigin: { horizontal: toolbarSide === 'left' ? 'right' : 'left', vertical: 'top' },
                          transformOrigin: { horizontal: toolbarSide === 'left' ? 'left' : 'right', vertical: 'top' },
                          PaperProps: {
                            sx: {
                              marginLeft: toolbarSide === 'left' ? 2 : 0,
                              marginRight: toolbarSide === 'right' ? 2 : 0,
                              background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(20px)',
                              WebkitBackdropFilter: 'blur(20px)',
                              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                            }
                          }
                        } : {
                          PaperProps: {
                            sx: {
                              background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(20px)',
                              WebkitBackdropFilter: 'blur(20px)',
                              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                            }
                          }
                        }}
                      >
                        {availableWebcams.map((webcam) => (
                          <MenuItem key={webcam.deviceId} value={webcam.deviceId}>
                            {webcam.label || `Webcam ${webcam.deviceId.slice(0, 8)}...`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                      </span>
                    </Tooltip>
                  )}
                  
                  {/* Live Stream Button - now handles both screen and webcam */}
                  <Tooltip title={
                    isSourceSwitching 
                      ? 'Switching video source...' 
                      : isStreaming 
                        ? 'Stop Live Stream' 
                        : `Start ${videoSource} Stream`
                  }>
                    <span>
                      <IconButton 
                        color={isStreaming ? 'error' : 'primary'} 
                        size="large" 
                        onClick={onLiveStream}
                        disabled={isSourceSwitching}
                      >
                        {isSourceSwitching ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : isStreaming ? (
                          <VideocamOffIcon />
                        ) : (
                          <VideocamIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  {/* Webcam-specific button for quick webcam capture */}
                  <Tooltip title={
                    isWebcamDeviceSwitching 
                      ? 'Switching webcam device...' 
                      : isWebcamStreaming 
                        ? 'Stop Webcam' 
                        : 'Start Webcam'
                  }>
                    <span>
                      <IconButton 
                        color={isWebcamStreaming ? 'error' : 'primary'} 
                        size="large" 
                        onClick={onWebcamStream}
                        disabled={isWebcamDeviceSwitching}
                        sx={{ 
                          display: videoSource === 'webcam' ? 'inline-flex' : 'none',
                          ml: 1,
                        }}
                      >
                        {isWebcamDeviceSwitching ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          <CameraAltIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title={isMicStreaming ? 'Stop Mic Stream (Raw Audio)' : 'Start Mic Stream (Raw Audio)'}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <IconButton 
                        color={isMicStreaming ? 'error' : 'primary'} 
                        size="large" 
                        onClick={onMicStream}
                      >
                        <MicIcon />
                      </IconButton>
                      {isMicStreaming && (
                        <div style={{
                          width: 40,
                          height: 12,
                          marginLeft: 4,
                          borderRadius: 6,
                          background: '#eee',
                          border: '1px solid #ccc',
                          overflow: 'hidden',
                          display: 'inline-block',
                          verticalAlign: 'middle',
                        }}>
                          <div style={{
                            width: `${Math.round((micVolume || 0) * 100)}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                            borderRadius: 6,
                            transition: 'width 0.1s',
                          }} />
                        </div>
                      )}
                    </span>
                  </Tooltip>
                  <Tooltip title={showTranscription ? 'Hide Transcription Window' : 'Show Transcription Window'}>
                    <span>
                      <IconButton 
                        color={showTranscription ? 'primary' : 'default'} 
                        size="large" 
                        onClick={onToggleTranscription}
                      >
                        <SubtitlesIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Select audio source (Mic, System, or Both)">
                    <span>
                      <FormControl size="small" sx={{ minWidth: 90, ml: 1 }}>
                        <Select
                          value={audioSource || 'mic'}
                          onChange={e => onAudioSourceChange(e.target.value as 'mic' | 'system' | 'both')}
                          displayEmpty
                          inputProps={{ 'aria-label': 'Audio Source' }}
                      sx={{ 
                        fontSize: 13,
                        background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 1,
                        '& .MuiSelect-select': {
                          color: darkMode ? 'white' : 'inherit',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                      MenuProps={isVertical ? {
                        anchorOrigin: { horizontal: toolbarSide === 'left' ? 'right' : 'left', vertical: 'top' },
                        transformOrigin: { horizontal: toolbarSide === 'left' ? 'left' : 'right', vertical: 'top' },
                        PaperProps: {
                          sx: {
                            marginLeft: toolbarSide === 'left' ? 2 : 0,
                            marginRight: toolbarSide === 'right' ? 2 : 0,
                            background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                          }
                        }
                      } : {
                        PaperProps: {
                          sx: {
                            background: darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                          }
                        }
                      }}
                    >
                      <MenuItem value="mic">Mic</MenuItem>
                      <MenuItem value="system">System</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                    </Select>
                  </FormControl>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Tooltip title={darkMode ? 'Light mode' : 'Dark mode'}>
                    <IconButton 
                      onClick={onThemeToggle} 
                      color="inherit" 
                      size="large" 
                      aria-label="Toggle theme"
                    >
                      {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                    </IconButton>
                  </Tooltip>
                  {!isVertical && <Box sx={{ flexGrow: 1 }} />}
                  <Tooltip title="Account menu">
                    <IconButton
                      size="large"
                      edge={isVertical ? undefined : "end"}
                      aria-label="account of current user"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      onClick={handleMenu}
                      color="inherit"
                    >
                      {user && user.picture ? (
                        <img src={user.picture} alt="Profile" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                      ) : (
                        <AccountCircle />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={
                      isVertical
                        ? { vertical: 'top', horizontal: toolbarSide === 'left' ? 'right' : 'left' }
                        : { vertical: 'bottom', horizontal: 'right' }
                    }
                    keepMounted
                    transformOrigin={
                      isVertical
                        ? { vertical: 'top', horizontal: toolbarSide === 'left' ? 'left' : 'right' }
                        : { vertical: 'top', horizontal: 'right' }
                    }
                    PaperProps={{
                      sx: {
                        marginLeft: isVertical && toolbarSide === 'left' ? 2 : 0,
                        marginRight: isVertical && toolbarSide === 'right' ? 2 : 0,
                      }
                    }}
                    open={open}
                    onClose={handleClose}
                  >
                    <MenuItem disabled>
                      <ListItemIcon>
                        {user && user.picture ? (
                          <img src={user.picture} alt="Profile" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                        ) : (
                          <AccountCircle fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText>
                        {user ? (
                          <>
                            {user.name && <span>{user.name}</span>}
                            {user.email && <span style={{ display: 'block', fontSize: 13, color: '#888' }}>{user.email}</span>}
                          </>
                        ) : (
                          'Account: Not logged in'
                        )}
                      </ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleSettings}>
                      <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Settings</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Log Out</ListItemText>
                    </MenuItem>
                  </Menu>
                </Box>
              )}
            </Draggable>
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default Toolbar; 